import useSWR from "swr";
import { getValidSupabaseAccessToken } from "@/lib/supabase-access-token";

export type ReferralMeData = {
  code: string;
  partnerUserId: string;
};

export type ReferralMeState =
  | { kind: "ok"; data: ReferralMeData }
  | { kind: "not_partner" }
  | { kind: "unauthenticated" };

async function fetchReferralMeState(): Promise<ReferralMeState> {
  const accessToken = await getValidSupabaseAccessToken();
  if (!accessToken) {
    return { kind: "unauthenticated" };
  }

  const response = await fetch("/api/referrals/me", {
    headers: { Authorization: `Bearer ${accessToken}` },
  });

  if (response.status === 403) {
    return { kind: "not_partner" };
  }
  if (response.status === 401) {
    return { kind: "unauthenticated" };
  }
  if (!response.ok) {
    throw new Error("Failed to verify referral access");
  }

  const json = (await response.json()) as ReferralMeData;
  return { kind: "ok", data: json };
}

/**
 * Server-backed market partner check (matches DB + creates referral code if needed).
 * Use for navigation and /refer gating when client profile may omit `partner_type`.
 */
export function useReferralMe(enabled: boolean) {
  const { data, error, isLoading, mutate } = useSWR(
    enabled ? "referral-me" : null,
    fetchReferralMeState,
    {
      // Shared key + long dedupe keeps duplicate header mounts from refetching.
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      revalidateIfStale: false,
      shouldRetryOnError: false,
      dedupingInterval: 5 * 60_000,
    },
  );

  return {
    state: data,
    isLoading,
    error: error instanceof Error ? error : null,
    refresh: mutate,
    isPartner: data?.kind === "ok",
    referralCode: data?.kind === "ok" ? data.data.code : null,
  };
}
